import cn from 'classnames'
import _ from 'lodash'
import {FC, RefObject} from 'react'

import {Button} from '@/components/common/content/Button'
import {ButtonSize, ButtonVariant} from '@/components/common/content/ButtonTypes'
import {Text, TextFontWeight, TextVariant} from '@/components/common/content/Text'
import {ArrowIcon} from '@/components/common/icons/ArrowIcon'
import {ChevronIcon} from '@/components/common/icons/ChevronIcon'
import {usePaginationContent} from '@/content/pagination'
import {useTranslation} from '@/i18n'
import {theme} from '@/theme/theme'
import {MOBILE_SCREENSIZE, USER_SMALL_DESKTOP_SCREENSIZE} from '@/utils/styles/breakpoints'
import {getScrollBehavior} from '@/utils/ui/scrollBehavior'
import {scrollIntoView} from '@/utils/ui/scrollIntoView'

const getPaginationItems = (numberOfPages: number, currentPage: number) => {
  const pageItems: (number | string)[] = []

  if (numberOfPages <= 6) {
    // (first + 4-page window + last)
    pageItems.push(..._.range(1, numberOfPages + 1))
  } else {
    // Always show the first page
    pageItems.push(1)

    if (currentPage <= 4) {
      // Show the first 5 pages when near the beginning
      pageItems.push(..._.range(2, 6), '...', numberOfPages)
    } else if (currentPage >= numberOfPages - 3) {
      // If near the end, show last 5 pages
      pageItems.push('...', ..._.range(numberOfPages - 4, numberOfPages + 1))
    } else {
      // For middle pages, show 3 pages around the current page
      pageItems.push('...', currentPage - 1, currentPage, currentPage + 1, '...', numberOfPages)
    }
  }

  return pageItems
}

type PaginationProps = {
  sectionRef: RefObject<HTMLDivElement>
  sectionOffset?: number
  currentPage: number
  numberOfPages: number
  setCurrentPage: (page: number) => void
  // Disables the controls during a server round trip, so a second click can't queue another.
  isPending?: boolean
}

export const Pagination: FC<PaginationProps> = ({
  sectionRef,
  sectionOffset = 0,
  currentPage,
  numberOfPages,
  setCurrentPage,
  isPending = false,
}) => {
  const {t} = useTranslation()
  const {goUpTitle, previousTitle, nextTitle} = usePaginationContent()

  const handlePageChange = (page: number) => {
    if (isPending || page < 1 || page > numberOfPages) {
      return
    }
    setCurrentPage(page)
    scrollIntoView(sectionRef, sectionOffset)
  }

  const paginationItems = getPaginationItems(numberOfPages, currentPage)

  return (
    <>
      <div className="pagination">
        <Button
          type="button"
          icon={<ChevronIcon variant="top" />}
          variant={ButtonVariant.tertiary}
          size={ButtonSize.medium}
          textVariant={TextVariant.subtitle13}
          onClick={() => window.scrollTo({top: 0, behavior: getScrollBehavior()})}
        >
          {t(goUpTitle)}
        </Button>
        {/*
         * Hidden, not removed: dropping the unavailable button re-centres the group and shifts
         * the other one sideways on click. `visibility: hidden` also skips it in tab order.
         */}
        <div className="buttons">
          <div className={cn('button', currentPage <= 1 && 'reserved')}>
            <Button
              type="button"
              variant={ButtonVariant.primary}
              size={ButtonSize.medium}
              textVariant={TextVariant.subtitle13}
              icon={<ArrowIcon color={theme.palette.text.white} variant="left" />}
              iconPosition="left"
              fullWidth
              disabled={isPending}
              onClick={() => handlePageChange(currentPage - 1)}
            >
              {t(previousTitle)}
            </Button>
          </div>
          <div className={cn('button', currentPage >= numberOfPages && 'reserved')}>
            <Button
              type="button"
              variant={ButtonVariant.primary}
              size={ButtonSize.medium}
              textVariant={TextVariant.subtitle13}
              icon={<ArrowIcon color={theme.palette.text.white} />}
              fullWidth
              disabled={isPending}
              onClick={() => handlePageChange(currentPage + 1)}
            >
              {t(nextTitle)}
            </Button>
          </div>
        </div>
        <div className="pages">
          {paginationItems.map((item, index) => (
            <div key={index} className="page">
              {typeof item === 'number' ? (
                <Button
                  type="button"
                  onClick={() => handlePageChange(item)}
                  variant={ButtonVariant.tertiary}
                  size={ButtonSize.medium}
                  textVariant={TextVariant.subtitle13}
                  hasBorder={item === currentPage}
                  fullWidth
                  disabled={isPending}
                >
                  {String(item)}
                </Button>
              ) : (
                <Text variant={TextVariant.text14} fontWeight={TextFontWeight.bold}>
                  {item}
                </Text>
              )}
            </div>
          ))}
        </div>
      </div>

      <style jsx>{`
        .pagination {
          display: flex;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: ${theme.spacing[3]};
        }
        .buttons {
          display: flex;
          gap: ${theme.spacing[2]};
        }
        .button {
          min-width: 110px;
        }
        .button.reserved {
          visibility: hidden;
        }
        .pages {
          display: flex;
          gap: ${theme.spacing[0.5]};
        }
        .page {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 40px;
        }
        @media ${USER_SMALL_DESKTOP_SCREENSIZE} {
          .pagination {
            flex-direction: column;
            align-items: center;
          }
        }
        @media ${MOBILE_SCREENSIZE} {
          .button {
            min-width: inherit;
            flex: 1;
          }
          .button.reserved {
            display: none;
          }
          .page {
            width: 36px;
          }
        }
      `}</style>
    </>
  )
}
